import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

export type Program = Tables<'programs'>;

export interface PublicCohort {
  id: string;
  name: string;
  program_id: string;
  start_date: string | null;
  end_date: string | null;
  price: number | null;
  currency: string | null;
  status: string | null;
  enrollment_open: boolean;
  is_perpetual?: boolean;
}

export function usePrograms() {
  return useQuery({
    queryKey: ['programs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('programs')
        .select('*')
        .order('level', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return data as Program[];
    },
  });
}

export function useActivePrograms() {
  return useQuery({
    queryKey: ['programs', 'active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('programs')
        .select('*')
        .in('status', ['active', 'public'])
        .order('name', { ascending: true });
      if (error) throw error;
      return data as Program[];
    },
  });
}

export function usePublicPrograms() {
  return useQuery({
    queryKey: ['programs', 'public'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('programs')
        .select('*')
        .eq('status', 'public')
        .order('level', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return data as Program[];
    },
  });
}

export function useProgramBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ['programs', 'slug', slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('programs')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();
      if (error) throw error;
      return data as Program | null;
    },
    enabled: !!slug,
  });
}

export function usePublicCohorts(programId: string | undefined) {
  return useQuery({
    queryKey: ['cohorts', 'public', programId],
    queryFn: async () => {
      if (!programId) return [];
      const { data, error } = await supabase
        .from('cohorts')
        .select('*')
        .eq('program_id', programId)
        .eq('is_public', true)
        .eq('status', 'active');
      if (error) throw error;
      return (data || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        program_id: c.program_id,
        start_date: c.start_date,
        end_date: c.end_date,
        price: c.price,
        currency: c.currency,
        status: c.status,
        enrollment_open: c.enrollment_open ?? false,
        is_perpetual: c.is_perpetual ?? false,
      })) as PublicCohort[];
    },
    enabled: !!programId,
  });
}

export function useAllOpenCohorts() {
  return useQuery({
    queryKey: ['cohorts', 'public', 'open'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cohorts')
        .select('*')
        .eq('is_public', true)
        .eq('status', 'active')
        .eq('enrollment_open', true);
      if (error) throw error;
      return (data || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        program_id: c.program_id,
        start_date: c.start_date,
        end_date: c.end_date,
        price: c.price,
        currency: c.currency,
        status: c.status,
        enrollment_open: c.enrollment_open ?? false,
      })) as PublicCohort[];
    },
  });
}
